import React, { useRef, useState } from 'react';
import {
  Datagrid,
  FunctionField,
  ReferenceField,
  TextField,
  useRefresh,
} from 'react-admin';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import LayersIcon from '@material-ui/icons/Layers';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbed from '../../Components/ReactAdmin/List/ListPageTabbed';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api } from '../../services';
import { PRIMARY_COLOR } from '../../utils';
import { STATUS_ATIVO, STATUS_INATIVO } from '../../utils/constants';
import { PERMISSAO_TELA_ENDERECOS_EXPOSICAO_LISTAR } from '../../utils/dadosTelas';
import DialogoCreateEdit from './DialogoCreateEdit';
import DialogoShow from './DialogoShow';
import Filter from './Filter';

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'red',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
      color: 'red !important',
    },
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const tabs = [
  { id: 'ativos', name: 'Ativos', filter: { status: STATUS_ATIVO } },
  { id: 'inativos', name: 'Inativos', filter: { status: STATUS_INATIVO } },
];

const NUMERO_ANDARES_EXIBIDAS = 3;

function getNomeColunaAndar(andar) {
  if (andar === 1) return 'Térreo';
  return `${andar - 1}º Aéreo`;
}

function getLabelRepeticao(repeticao) {
  if (repeticao === 1) return 'Única';
  if (repeticao === 2) return 'Duplicado';
  if (repeticao === 3) return 'Triplicado';
  if (repeticao > 3) return 'Mais de 3';
  return '';
}

function getCoresRepeticao(repeticao) {
  if (repeticao === 1)
    return { bg: '#e8f5e9', borda: '#43a047', texto: '#2e7d32' };
  if (repeticao === 2)
    return { bg: '#e3f2fd', borda: '#1e88e5', texto: '#1565c0' };
  if (repeticao === 3)
    return { bg: '#fff3e0', borda: '#fb8c00', texto: '#e65100' };
  if (repeticao > 3)
    return { bg: '#f3e5f5', borda: '#8e24aa', texto: '#6a1b9a' };
  return { bg: '#f5f5f5', borda: '#bdbdbd', texto: '#616161' };
}

function renderCelulaAndar(record, andar) {
  if (andar > (record.quantidade_andares || 1)) {
    return (
      <div
        style={{
          borderRadius: '8px',
          padding: '8px 10px',
          minWidth: '160px',
          textAlign: 'center',
          color: '#bdbdbd',
        }}
      >
        <Typography variant="caption" style={{ fontStyle: 'italic' }}>
          Sem andar
        </Typography>
      </div>
    );
  }

  const ocupante = (record.andaresProdutos || []).find(
    (ap) => ap.andar === andar,
  );

  if (ocupante && ocupante.produto) {
    // A indicação de repetição (Única/Duplicado/etc.) só faz sentido no
    // térreo de um endereço marcado como exposição em loja — é o único caso
    // que essa contagem realmente considera no backend.
    const mostrarRepeticao = andar === 1 && !!record.terreo_exposicao;
    const cores = mostrarRepeticao
      ? getCoresRepeticao(ocupante.produto.repeticao_exposicao_na_loja)
      : { bg: '#fafafa', borda: '#e0e0e0', texto: '#424242' };
    return (
      <div
        style={{
          backgroundColor: cores.bg,
          border: `1px solid ${cores.borda}`,
          borderRadius: '8px',
          padding: '8px 10px',
          minWidth: '160px',
        }}
      >
        <Typography
          variant="body2"
          style={{ fontWeight: 'bold', color: cores.texto }}
        >
          {ocupante.produto.nome}
        </Typography>
        {mostrarRepeticao && (
          <Typography variant="caption" style={{ color: cores.texto }}>
            {`Exposição: ${getLabelRepeticao(
              ocupante.produto.repeticao_exposicao_na_loja,
            )}`}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px dashed #bdbdbd',
        borderRadius: '8px',
        padding: '8px 10px',
        minWidth: '160px',
        textAlign: 'center',
        color: '#9e9e9e',
      }}
    >
      <AddCircleOutlineIcon fontSize="small" />
      <Typography variant="caption" style={{ display: 'block' }}>
        Vazio
      </Typography>
    </div>
  );
}

const List = (props) => {
  const refDialogoConfirmacao = useRef(null);
  const refresh = useRefresh();
  const { enqueueSnackbar } = useSnackbar();
  const classes2 = useStyles();
  const refDialogoShow = useRef(null);
  const [dialogo, setDialogo] = useState({
    open: false,
    id: null,
  });

  function handleCriar() {
    setDialogo({
      open: true,
      id: null,
    });
  }

  function handleEdit({ id }) {
    setDialogo({
      open: true,
      id,
    });
  }

  function handleShow({ id }) {
    if (refDialogoShow.current) {
      refDialogoShow.current.handleOpen(id);
    }
  }

  async function ativar(id) {
    const response = await api.put(
      `/enderecos_exposicao_mercadorias/status/ativar/${id}`,
    );
    if (response.data.status) {
      handleClose();
    } else {
      enqueueSnackbar(response.data.message, { variant: 'error' });
    }
  }
  async function desativar(id) {
    const response = await api.put(
      `/enderecos_exposicao_mercadorias/status/inativar/${id}`,
    );
    if (response.data.status) {
      handleClose();
    } else {
      enqueueSnackbar(response.data.message, { variant: 'error' });
    }
  }
  async function deletar(id) {
    const response = await api.put(
      `/enderecos_exposicao_mercadorias/status/deletar/${id}`,
    );
    if (response.data.status) {
      handleClose();
    } else {
      enqueueSnackbar(response.data.message, { variant: 'error' });
    }
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        return ativar(id);
      case 2:
        return desativar(id);
      case 3:
        return deletar(id);
      default:
    }
  }

  const handleClose = () => {
    refresh();
  };

  const toolbarActions = <ActionsList handleCriar={handleCriar} {...props} />;

  function handleAtivar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'REATIVAR ENDEREÇO DE EXPOSIÇÃO DE MERCADORIA',
        'Tem certeza que deseja reativar?',
        {
          id,
          codigo: 1,
        },
      );
    }
  }

  function handleInativar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'INATIVAR ENDEREÇO DE EXPOSIÇÃO DE MERCADORIA',
        'Tem certeza que deseja inativar?',
        {
          id,
          codigo: 2,
        },
      );
    }
  }

  function handelDeletar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'DELETAR ENDEREÇO DE EXPOSIÇÃO DE MERCADORIA',
        'Tem certeza que deseja deletar?',
        {
          id,
          codigo: 3,
        },
      );
    }
  }

  const renderGrid = ({ listProps, classes, listContext }) => (
    <Datagrid {...props}>
      <TextField
        source="codigo"
        label="Descrição"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
      />
      <ReferenceField
        source="mapa_loja_id"
        reference="mapas_loja"
        label="Mapa"
        link={false}
        sortable={false}
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        emptyText="-"
      >
        <TextField source="nome" />
      </ReferenceField>
      {Array.from({ length: NUMERO_ANDARES_EXIBIDAS }).map((_, index) => {
        const andar = index + 1;
        return (
          <FunctionField
            key={`andar-${andar}`}
            source={`andar_${andar}`}
            label={getNomeColunaAndar(andar)}
            headerClassName={classes2.dataHeader}
            cellClassName={classes2.dataCell}
            render={(record) => renderCelulaAndar(record, andar)}
          />
        );
      })}
      <FunctionField
        source="quantidade_andares"
        label="Andares"
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        sortable={false}
        render={(record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <LayersIcon fontSize="small" style={{ color: '#757575' }} />
            <span>{record.quantidade_andares}</span>
          </div>
        )}
      />
      <ActionsField
        textAlign="right"
        label="Ações"
        sortable={false}
        headerClassName={classes2.dataHeader}
        cellClassName={classes2.dataCell}
        maxAcoesVisiveis={2}
        actions={[
          {
            title: 'Editar',
            icon: <EditOutlinedIcon />,
            show: (r) => r.status === STATUS_ATIVO,
            onClick: (r) => handleEdit(r),
          },
          {
            title: 'Detalhes',
            icon: <VisibilityIcon />,
            show: () => true,
            onClick: (r) => handleShow(r),
          },
          {
            title: 'Ativar',
            icon: <CheckCircleOutlineIcon />,
            show: (r) => r.status === STATUS_INATIVO,
            onClick: (r) => handleAtivar(r.id),
          },
          {
            title: 'Desativar',
            icon: <ClearIcon />,
            show: (r) => r.status === STATUS_ATIVO,
            onClick: (r) => handleInativar(r.id),
          },
          {
            title: 'Excluir',
            icon: <DeleteOutlineIcon />,
            show: (r) => r.status === STATUS_INATIVO,
            onClick: (r) => handelDeletar(r.id),
          },
        ]}
      />
    </Datagrid>
  );

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_ENDERECOS_EXPOSICAO_LISTAR}>
      <>
        <ListPageTabbed
          title="Endereços"
          filters={<Filter />}
          toolbarActions={toolbarActions}
          renderGrid={renderGrid}
          perPage={50}
          //listProps={props}
          tabs={tabs}
          filterDefaultValues={{ status: STATUS_ATIVO }}
          listProps={{ sort: { field: 'codigo', order: 'asc' }, ...props }}
        />

        <DialogoConfirmacao
          ref={refDialogoConfirmacao}
          handleConfirma={handleConfirma}
        />
        <DialogoCreateEdit
          open={dialogo.open}
          id={dialogo.id}
          onClose={() => {
            setDialogo({
              open: false,
              id: null,
            });
            handleClose();
          }}
          isDialogoConfirmacaoActions
        />
        <DialogoShow ref={refDialogoShow} handleClose={refresh} />
      </>
    </ProtegerPagina>
  );
};

export default List;
